import React, { useState, useEffect, FC, useContext, useMemo } from 'react';
import {
  alpha,
  Box,
  CircularProgress,
  createTheme,
  CssBaseline,
  hexToRgb,
  LinearProgress,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import SurveyFooter from '../../components/SurveyFooter/SurveyFooter';
import SurveyService from '../../services/SurveyService';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import SurveyHeader from '../../components/SurveyHeader/SurveyHeader';
import Feedback from '../../components/NetPromoterScoreContent/Feedback';
import Section from '../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import i18n from 'i18next';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import {
  scopeSurveyCustomCss,
  SURVEY_CUSTOM_CSS_SCOPE_CLASS,
  SURVEY_CUSTOM_CSS_SCOPE_SELECTOR,
} from '../../shared/SurveyCustomCss';

interface Props {
  previewSurvey?: ISurvey;
}

const NetPromoterScorePage: FC<Props> = ({ previewSurvey }) => {
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();

  const [npsPage, setNpsPage] = useState<ISurvey | null>(previewSurvey ?? null);
  const localTranslation = useLocalTranslation(npsPage ? npsPage : { lang: 'de' });
  const [currentId, setCurrentId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (previewSurvey) return;
    const controller = new AbortController();
    const splitUrl = window.location.href.split('/');
    const extractValue = splitUrl[splitUrl.length - 1].toString();

    const getSurveyData = async () => {
      try {
        const response = await SurveyService.getSurveyPublic(
          extractValue,
          'nps',
          setLoading,
          controller.signal
        );

        if (response) {
          setCurrentId(response.id);
          setNpsPage(response);

          if (response.language) {
            i18n.changeLanguage(response.language);
          }
        } else {
          setError(true);
        }
      } catch (exc) {
        console.error('Fehler beim Abrufen der Umfrage:', exc);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    getSurveyData();

    return () => {
      controller.abort();
    };
  }, [currentId, previewSurvey]);

  useEffect(() => {
    setCurrentView(t('nps_survey'));
    document.title = npsPage?.label ? npsPage.label : `${t('app_branding')} ${t('nps_survey')}`;
  }, [setCurrentView, t, npsPage]);

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  const effectiveFontFamily =
    npsPage?.customFont && npsPage.customFontFormat
      ? `"${npsPage.id}"`
      : '"Arial", "Helvetica", sans-serif';

  const customTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: npsPage?.primaryColor ? npsPage.primaryColor : theme.palette.primary.main,
          },
          divider: npsPage?.secondaryColor
            ? alpha(hexToRgb(npsPage.secondaryColor), 0.2)
            : theme.palette.divider,
        },
        typography: {
          fontFamily: effectiveFontFamily,
        },
      }),
    [
      effectiveFontFamily,
      npsPage?.primaryColor,
      npsPage?.secondaryColor,
      theme.palette.divider,
      theme.palette.primary.main,
    ]
  );

  const customFontCss =
    npsPage?.customFont && npsPage.customFontFormat
      ? `
          @font-face {
            font-family: "${npsPage.id}";
            src: url(${npsPage.customFont}) format("${getFontFormatByFileExtension(
          npsPage.customFontFormat as FontFileExtensions
        )}");
          }

          ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR},
          ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} * {
            font-family: ${effectiveFontFamily} !important;
          }
        `
      : `
          ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR},
          ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} * {
            font-family: ${effectiveFontFamily} !important;
          }
        `;

  if (!npsPage) {
    return (
      <Section>
        <Box
          sx={{
            display: 'flex',
            alignSelf: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          {error ? (
            <Typography>{t('survey_not_found_or_archived')}</Typography>
          ) : (
            <CircularProgress />
          )}
        </Box>
      </Section>
    );
  }

  const scopedCustomCss = scopeSurveyCustomCss(npsPage.customCss);

  return (
    <LocalTranslationContext.Provider value={localTranslation}>
      <CssBaseline />
      <Box
        className="survey-page-shell survey-page-shell--nps"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          backgroundColor: '#ffffff',
        }}
      >
        <ThemeProvider theme={customTheme}>
          <style>{customFontCss}</style>
          {scopedCustomCss && <style>{scopedCustomCss}</style>}
          {loading && <LinearProgress />}
          {error && <Typography>Error</Typography>}
          <Box
            className={SURVEY_CUSTOM_CSS_SCOPE_CLASS}
            sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
          >
            <Box
              className="survey-page survey-page--nps nps-page-root"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100%',
                alignItems: 'stretch',
                flex: 1,
                fontFamily: effectiveFontFamily,
              }}
            >
              <SurveyHeader
                logoLink={npsPage.logoLink}
                logo={npsPage.companyLogo}
              />
              <Feedback {...npsPage} />
            </Box>
          </Box>
        </ThemeProvider>

        <SurveyFooter
          company={npsPage.companyName}
          privacyPolicyLink={npsPage.privacyPolicyLink}
          imprintLink={npsPage.imprintLink}
        />
      </Box>
    </LocalTranslationContext.Provider>
  );
};

export default NetPromoterScorePage;
